/**
 * The filters a grid's toolbar offers, as the backend publishes them.
 *
 * A grid descriptor names the filters this requester may use, in the order
 * they should read, and — for a closed filter — every value it accepts. That
 * list is the whole vocabulary: a filter missing from it is one this requester
 * may not use and is never rendered, and a value missing from a filter's
 * options is not a value the list can be narrowed to. A client keeping its own
 * copy of either drifts from the backend, which is how a stat card describing
 * a bucket the toolbar had never heard of ended up filtering to a raw token.
 *
 * Per-option counts are deliberately not part of the descriptor: a count
 * changes every time the filter model changes while the vocabulary does not.
 * They arrive with the numbers instead, as the stats payload's `filter_counts`,
 * and are merged on here by value so one control reads from both.
 *
 * A filter the client has no control shape for is skipped rather than rendered
 * as an empty dropdown — the descriptor may legitimately publish a filter whose
 * op this toolbar cannot express yet, and a broken control is worse than an
 * absent one. Adding a shape here is what makes such a filter appear.
 *
 * Contains:
 * - buildDescriptorFilterControls — published filters (+ counts) → controls
 * - buildToolbarFilterDefs — controls → filter-bar defs over a filter model
 * - splitQuickFilterDefs — the defs the grid asked to sit inline, and the rest
 * - omitOwnedFilterControls — drop the params a page's own controls hold
 * - unofferedFilterValues — the held values this descriptor does not offer
 *
 * Returns plain JS — no JSX, no redux.
 */

import { resolveDescriptorLabel } from './resolveDescriptorLabel';

/** Ops whose value is a set, so the control offers several at once. */
const MULTI_VALUE_OPS = new Set(['in']);

/** Ops over a single free-typed value — a search box rather than a list. */
const FREE_VALUE_OPS = new Set(['icontains', 'istartswith', 'exact', 'iexact']);

/**
 * The control shape a published filter asks for, or null when this toolbar has
 * none for it. A closed vocabulary reads from its op whether the reader may
 * pick several values or exactly one; an open filter reads from its type.
 *
 * @param {object} filter - one published filter, in the descriptor's own shape.
 * @returns {?string} 'multi' | 'single' | 'boolean' | 'text', or null to skip.
 */
function controlType({ options, op, type }) {
  if (Array.isArray(options) && options.length > 0) {
    return MULTI_VALUE_OPS.has(op) ? 'multi' : 'single';
  }
  if (type === 'boolean') return 'boolean';
  return FREE_VALUE_OPS.has(op) ? 'text' : null;
}

/**
 * Index one param's counts by value. Values are compared as strings: a count
 * for load type 13 belongs to the option whose value is the id `'13'`.
 *
 * @param {Array<{value: *, count: number}>} [entries]
 * @returns {Map<string, number>}
 */
function countsByValue(entries) {
  const counts = new Map();
  for (const entry of entries ?? []) {
    if (entry?.value === undefined || entry?.value === null) continue;
    counts.set(String(entry.value), entry.count);
  }
  return counts;
}

/**
 * An option's copy. A `label_key` is the backend saying "this reads out of the
 * catalogue"; a `label` is text that is already human — a load type's own name,
 * which no catalogue holds — and stands in when the key resolves to nothing.
 * An option carrying neither reads as its own value, which at least names the
 * thing it filters to.
 *
 * @param {object} option
 * @param {?Function} translate
 * @param {Object<string, string>} [terms]
 * @returns {string}
 */
function optionLabel({ value, label_key: labelKey, label }, translate, terms) {
  const literal = label ?? String(value ?? '');
  return labelKey ? resolveDescriptorLabel(translate, labelKey, literal, terms) : literal;
}

/**
 * Turn the descriptor's published filters into the toolbar's control
 * definitions, in published order.
 *
 * @param {object[]} [filters] - the descriptor's `filters` array. Anything
 *   other than an array — including the absent key a descriptor emitted before
 *   the backend published filters at all — yields no controls, leaving a page's
 *   own filter UI as the whole toolbar.
 * @param {?Object<string, Array<{value: *, count: number}>>} [filterCounts] -
 *   the stats payload's `filter_counts`, keyed by param. Absent leaves every
 *   option countless.
 * @param {object} [context]
 * @param {?Function} [context.translate] - the i18n translator. Absent outside
 *   an i18n provider, where a descriptor's literals are the whole answer.
 * @param {Object<string, string>} [context.terms] - tenant vocabulary, so a
 *   filter's label may name the customer's nouns.
 * @returns {Array<{param: string, label: string, type: string,
 *   options: (Array<{value: *, label: string, count: (number|undefined)}>|undefined)}>}
 *   Control definitions; *filters* is never mutated. `options` is absent on a
 *   free-value control.
 */
export function buildDescriptorFilterControls(filters, filterCounts, { translate, terms } = {}) {
  if (!Array.isArray(filters)) return [];

  const controls = [];
  for (const filter of filters) {
    const { param, label_key: labelKey, label, options } = filter ?? {};
    if (!param) continue;
    const type = controlType(filter);
    if (!type) continue;

    const counts = countsByValue(filterCounts?.[param]);
    controls.push({
      param,
      // Where the grid asked this control to sit, and the meaning to draw on
      // it. Both are the grid's to state and the client's to render.
      quick: Boolean(filter?.quick),
      icon: filter?.icon ?? undefined,
      hideEmpty: Boolean(filter?.hide_empty),
      // Published only to a requester who may use it, so this is a badge,
      // not a gate: the control is one the customer never sees.
      staffOnly: Boolean(filter?.staff_only),
      // Where this filter rests: the value a reader starts on and returns to,
      // for a grid that opens on something narrower than everything. Absent
      // means it rests unset, which is most of them.
      defaultValue: filter?.default ?? undefined,
      // Same resolver the column headers and stat-card titles use, so a filter
      // and the column it narrows cannot disagree about the tenant's nouns.
      label: labelKey ? resolveDescriptorLabel(translate, labelKey, label ?? param, terms) : (label ?? param),
      type,
      options: Array.isArray(options)
        ? options.map(option => ({
            value: option?.value,
            label: optionLabel(option ?? {}, translate, terms),
            count: counts.get(String(option?.value)),
            // An option inherited from an enum column carries the column's
            // own hints, so a value reads the same in a cell and in the
            // control that selects it.
            icon: option?.icon ?? undefined,
            tone: option?.tone ?? undefined,
          }))
        : undefined,
    });
  }
  return controls;
}

/**
 * Turn controls into the filter-bar's own definitions, each reading and writing
 * one param of the card-filter model. Going through that model rather than
 * beside it is what keeps a stat card and the control over the same param from
 * ever disagreeing: the card writes the param, the control reads it back, and
 * the value the reader sees is the value the list was narrowed by.
 *
 * Only a closed vocabulary gets a definition. A free-value control has no
 * affordance in the filter bar, and a name search is already what the grid's
 * own search box does — a def the bar would draw as an empty dropdown is worse
 * than no def.
 *
 * @param {Array<object>} [controls] - from buildDescriptorFilterControls.
 * @param {{model: Object<string, *>, values: Function, setFilter: Function}} cardFilters -
 *   the grid's card-filter model (useGridCardFilters).
 * @param {?Function} [renderIcon] - icon name → element. Injected rather than
 *   imported so this module stays free of JSX and of any icon set; absent
 *   leaves every control unadorned, which is a complete filter bar.
 * @returns {Array<object>} Filter-bar definitions, in the controls' order.
 */
export function buildToolbarFilterDefs(controls, cardFilters, renderIcon) {
  return (
    (controls ?? [])
      // A vocabulary draws as a list of values; a yes/no filter draws as itself
      // and needs none. A free text box is the one shape the bar has no
      // affordance for — the grid's search box is already that question.
      .filter(control => Array.isArray(control.options) || control.type === 'boolean')
      .map(control => ({
        key: control.param,
        param: control.param,
        label: control.label,
        type: control.type,
        quick: control.quick,
        staffOnly: control.staffOnly,
        icon: renderIcon ? renderIcon(control.icon) : undefined,
        // The count travels beside the label, never inside it: the controls
        // right-align it in its own column, and a label carrying it could not be
        // echoed into an active-filter pill without carrying it there too.
        options: Array.isArray(control.options)
          ? offeredOptions(control, cardFilters).map(option => ({
              value: option.value,
              label: option.label,
              count: option.count,
              icon: option.icon,
              tone: option.tone,
            }))
          : undefined,
        // The bar reads a filter sitting on its grid's resting value as unset —
        // it is the state the reader was given, not one they chose, so it earns
        // no active pill.
        defaultValue: control.defaultValue,
        // A multi-select holds a list whichever shape the param was written in;
        // a single-select holds the token itself, and null when unapplied. A
        // filter that rests somewhere shows that value while the model holds
        // nothing, so clearing the control lands back on the grid's own
        // starting point rather than on an answer nobody asked for.
        value: restingValue(control, cardFilters),
        onChange: next => cardFilters.setFilter(control.param, next),
      }))
  );
}

/**
 * What a control displays: the model's value, or the grid's resting one.
 *
 * A held value is the reader's own answer and is shown whatever the grid
 * rests on. Absence is the resting value when the grid named one, because
 * that is the state the rows are actually in — the request carries the same
 * default — and a control showing "any" over a list of active structures
 * would be describing a filter nobody applied.
 *
 * @param {{param: string, type: string, defaultValue: (string|undefined)}} control
 * @param {{values: Function, model: Object<string, *>}} cardFilters
 * @returns {(string[]|string|null)}
 */
function restingValue(control, cardFilters) {
  const held = control.type === 'multi' ? cardFilters.values(control.param) : cardFilters.model?.[control.param];
  if (control.type === 'multi')
    return held.length > 0 || control.defaultValue === undefined ? held : [control.defaultValue];
  return held ?? control.defaultValue ?? null;
}

/**
 * Every resting value a descriptor names, for the params a model does not hold.
 *
 * The request carries these so the grid opens where it says it does, and so a
 * reader clearing a control lands on the same state rather than on everything.
 * A param the model holds is the reader's answer and is left alone.
 *
 * @param {object[]} [filters] - the descriptor's `filters` array.
 * @param {Object<string, *>} [model] - the card-filter model.
 * @returns {Object<string, string>} param → value, empty when none rests.
 */
export function restingFilterParams(filters, model) {
  const params = {};
  for (const filter of Array.isArray(filters) ? filters : []) {
    const { param, default: defaultValue } = filter ?? {};
    if (!param || defaultValue === undefined || defaultValue === null) continue;
    const held = model?.[param];
    if (held === undefined || held === null || (Array.isArray(held) && held.length === 0)) {
      params[param] = String(defaultValue);
    }
  }
  return params;
}

/**
 * The options a control offers, minus the empty ones it asked to leave out.
 *
 * A long vocabulary is mostly empty on any given day — thirty-six alarm
 * categories, three of them in play — and the grid says when that is true of
 * one. A value the reader has already selected stays whatever its count, or
 * selecting it would make it vanish before they could clear it, and a control
 * with no counts at all keeps every option: an absent count is not a zero.
 *
 * @param {{options: Array<object>, hideEmpty: boolean, param: string}} control
 * @param {{values: Function}} cardFilters
 * @returns {Array<object>}
 */
function offeredOptions(control, cardFilters) {
  if (!control.hideEmpty) return control.options ?? [];
  const selected = new Set((cardFilters?.values?.(control.param) ?? []).map(String));
  return control.options.filter(
    option => option.count === undefined || option.count > 0 || selected.has(String(option.value)),
  );
}

/**
 * Split defs into the ones the grid asked to sit inline and the rest.
 *
 * Which handful of filters a reader reaches for constantly is a property of
 * the grid, so the backend states it and no page keeps its own list. Where the
 * two groups are drawn — chips in the bar, a popover behind "more filters" —
 * stays the client's business.
 *
 * @param {Array<{quick: (boolean|undefined)}>} [defs]
 * @returns {{quick: Array<object>, more: Array<object>}} Both in the defs' own
 *   order; *defs* is never mutated.
 */
export function splitQuickFilterDefs(defs) {
  const quick = [];
  const more = [];
  for (const def of defs ?? []) {
    (def?.quick ? quick : more).push(def);
  }
  return { quick, more };
}

/**
 * The values a filter model holds that this descriptor does not offer.
 *
 * A filter model outlives the descriptor it was built against: it is
 * remembered across visits, it rides in a bookmarked URL, and the vocabulary
 * behind it is per customer type — the load types one customer runs are not
 * the ones the next customer runs. A value the grid no longer offers is not a
 * narrow result, it is a refused request, so the ones that have gone stale are
 * named here and cleared before they are sent.
 *
 * Only a closed vocabulary can answer this. A free-value filter accepts
 * anything, and a filter absent from the descriptor is one this requester may
 * not use at all — that one is stale too.
 *
 * @param {object[]} [filters] - the descriptor's published `filters`.
 * @param {Object<string, *>} [model] - the card-filter model, param → value.
 * @returns {Array<{param: string, value: (string|undefined)}>} One entry per
 *   stale value; `value` is undefined when the whole param has gone.
 */
export function unofferedFilterValues(filters, model) {
  if (!Array.isArray(filters) || !model) return [];
  const published = new Map(filters.map(filter => [filter?.param, filter]));
  const stale = [];
  for (const [param, held] of Object.entries(model)) {
    const filter = published.get(param);
    if (!filter) {
      stale.push({ param, value: undefined });
      continue;
    }
    if (!Array.isArray(filter.options) || filter.options.length === 0) continue;
    const offered = new Set(filter.options.map(option => String(option?.value)));
    for (const value of Array.isArray(held) ? held : [held]) {
      if (value !== undefined && value !== null && !offered.has(String(value))) {
        stale.push({ param, value: String(value) });
      }
    }
  }
  return stale;
}

/**
 * Drop the controls whose param a page already offers its own control for. One
 * param drawing two controls is the failure this prevents — the descriptor's
 * list grows as the backend publishes more of it, and a page keeping a bespoke
 * control (one carrying an affordance the descriptor cannot state, such as the
 * status swatches) would otherwise find itself with both.
 *
 * @param {Array<{param: string}>} [controls]
 * @param {Iterable<string>} [ownedParams]
 * @returns {Array<object>} A new array; *controls* is never mutated.
 */
export function omitOwnedFilterControls(controls, ownedParams) {
  const owned = new Set(ownedParams ?? []);
  if (owned.size === 0) return controls ?? [];
  return (controls ?? []).filter(control => !owned.has(control.param));
}
